import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NavigationStateService } from '../../../../core/services/navigation/navigation-state.service';
import { PinnedModulesService } from '../../../../core/services/navigation/pinned-modules.service';
import { TOP_BAR_MODULES, TopBarModule } from '../../../../configs/top-bar-navigation.config';
import { RoleService } from '../../../../core/services/role/role.service';
import { RtoAccessService } from '../../../../core/services/rto/rto-access.service';
import { CacheService } from '../../../../core/services/cache/cache.service';
import { UserType } from '../../../interfaces';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { IcPinComponent } from '../../Icons/ic-pin/ic-pin';
import { CloseIcon } from '../../Icons/ic-close/ic-close';
import { IcChevronDownComponent } from '../../Icons/ic-chevron-down/ic-chevron-down';
import { ModuleIconComponent } from '../module-icon/module-icon.component';

/**
 * MegaMenuComponent component.
 * Provides UI presentation and event handling for mega menu component.
 */
@Component({
  selector: 'app-mega-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, IcPinComponent, CloseIcon, IcChevronDownComponent, ModuleIconComponent],
  templateUrl: './mega-menu.component.html',
  styleUrl: './mega-menu.component.scss',
})
export class MegaMenuComponent {
  /** Injected service for navigation state. */
  readonly navigationState = inject(NavigationStateService);
  /** Injected service for pinned service. */
  readonly pinnedService = inject(PinnedModulesService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for element ref. */
  private elementRef = inject(ElementRef);
  /** Injected service for role. */
  private role = inject(RoleService);
  /** Injected service for rto access service. */
  private rtoAccessService = inject(RtoAccessService);
  /** Current-user cache used to tailor Global Modules screens. */
  private readonly cacheService = inject(CacheService);

  /** Modules available to the user in the top-navigation mega menu. */
  readonly modules = computed<TopBarModule[]>(() => {
    const userType = this.cacheService.currentUser()?.userType;
    const hideRoles =
      userType === UserType.broker || userType === UserType.client;
    if (!hideRoles) {
      return TOP_BAR_MODULES;
    }
    return TOP_BAR_MODULES.map((module) =>
      module.key === 'settings'
        ? {
            ...module,
            screens: module.screens.filter((screen) => screen.key !== 'roles'),
          }
        : module
    );
  });

  /**
   * Reference to the menu panel element or component.
   */
  readonly menuPanel = viewChild<ElementRef>('menuPanel');

  /** Close on Escape */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.navigationState.closeMegaMenu();
  }

  /** Close when clicking outside the menu panel */
  onOverlayClick(event: Event): void {
    const panel = this.menuPanel()?.nativeElement;
    if (panel && !panel.contains(event.target as Node)) {
      this.navigationState.closeMegaMenu();
    }
  }

  /** Navigate to a module's screen */
  onScreenClick(
    moduleKey: string,
    screenKey: string,
    route: string,
    queryParams?: Record<string, string>
  ): void {
    if (
      !route.startsWith('/dashboard') &&
      (moduleKey === 'rto' || route.startsWith('/rto') || route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      this.navigationState.closeMegaMenu();
      return;
    }
    this.router.navigate([route], { queryParams: queryParams ?? {} });
    this.navigationState.closeMegaMenu();
  }

  /** Navigate to a module's default route */
  onModuleClick(module: TopBarModule): void {
    if (
      !module.route.startsWith('/dashboard') &&
      (module.key === 'rto' || module.route.startsWith('/rto') || module.route.includes('rto')) &&
      !this.role.isRtoEnabledForCurrentUser()
    ) {
      this.rtoAccessService.openRtoNotConfiguredModal();
      this.navigationState.closeMegaMenu();
      return;
    }
    this.router.navigate([module.route]);
    this.navigationState.closeMegaMenu();
  }

  /** Toggle a module's pinned state */
  onTogglePin(event: Event, moduleKey: string, screenKey: string): void {
    event.stopPropagation();
    this.pinnedService.togglePin(moduleKey, screenKey);
  }

  /** Check if a module is the currently active one */
  isCurrentModule(moduleKey: string): boolean {
    return this.navigationState.currentModule()?.key === moduleKey;
  }

  /** Check if a screen is the currently active one */
  isCurrentScreen(moduleKey: string, screenKey: string): boolean {
    return (
      this.navigationState.currentModule()?.key === moduleKey &&
      this.navigationState.currentScreen()?.key === screenKey
    );
  }

  /** Check if a screen is pinned */
  isPinned(moduleKey: string, screenKey: string): boolean {
    return this.pinnedService.isPinned(moduleKey, screenKey);
  }

  /**
   * Handles on close logic.
   */
  onClose(): void {
    this.navigationState.closeMegaMenu();
  }
}
